import React, { useEffect, useRef, useState } from 'react';
import type { ProductDesignerConfig } from '@customedge/shared';
import { useProductSetup } from '../hooks/useProductSetup';
import { useDesignStore } from '../store/designStore';
import { buildPreviewDataUrl } from '../lib/designDownload';

// `?mode=capture&design={id}` — a headless, self-closing render used only to
// produce the Design Guide PDF's per-side preview thumbnail. Mirrors
// DesignDownloadRoot.tsx's own `?mode=download` almost exactly (same public
// GET /api/designs/:id/widget + GET /api/products/:sku/config load, same
// useProductSetup + loadFromTemplate setup) — see that file's header comment
// for the full rationale on why this reuses the real Fabric canvas instead of
// a server-side re-implementation. The one real difference: this renders
// EVERY side's own buildPreviewDataUrl() (the same function the live
// "Preview" tab uses, product-photo-composited, at real pixel position) and
// POSTs the results to POST /api/designs/:id/capture-guide-preview instead of
// triggering a browser download — designGuideService.ts then prefers that
// stored image over sidePreviewCompositor.ts's server-side approximation.
//
// Embedded by the admin Order Detail page as a hidden (0×0) iframe alongside
// the existing visible `?mode=widget` one, so a capture happens automatically
// whenever an admin opens an order — no separate trigger/cron/webhook needed.
// A design not yet captured (order never opened in admin) simply falls back
// to the pre-existing base_high-based preview, same as before this existed.
export function DesignCaptureRoot({ designId }: { designId: string }) {
  const [config, setConfig] = useState<ProductDesignerConfig | null>(null);
  const [jsonObjects, setJsonObjects] = useState<Record<string, any> | null>(null);
  const [colorVariantId, setColorVariantId] = useState<string | null | undefined>(undefined);
  const [printMethod, setPrintMethod] = useState<string | null | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const widgetRes = await fetch(`/api/designs/${encodeURIComponent(designId)}/widget`);
        if (!widgetRes.ok) throw new Error(`HTTP ${widgetRes.status}`);
        const widget = await widgetRes.json();

        const configRes = await fetch(`/api/products/${encodeURIComponent(widget.productSku)}/config`);
        if (!configRes.ok) throw new Error(`HTTP ${configRes.status}`);
        const configData = await configRes.json();

        if (!cancelled) {
          setJsonObjects(widget.jsonObjects);
          setConfig(configData);
          setColorVariantId(widget.colorVariantId);
          setPrintMethod(widget.printMethod);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [designId]);

  // Silent no-op on failure — this is a background best-effort enhancement of
  // the Design Guide PDF, not a user-facing surface. A broken/missing design
  // must not show anything inside its host page's hidden iframe.
  if (failed || !config || !jsonObjects) return null;

  return (
    <DesignCaptureInner
      designId={designId}
      config={config}
      jsonObjects={jsonObjects}
      colorVariantId={colorVariantId}
      printMethod={printMethod}
    />
  );
}

function DesignCaptureInner({ designId, config, jsonObjects, colorVariantId, printMethod }: {
  designId: string;
  config: ProductDesignerConfig;
  jsonObjects: Record<string, any>;
  colorVariantId?: string | null;
  printMethod?: string | null;
}) {
  useProductSetup(config, { colorVariantId, printMethod });
  const { sides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey, loadFromTemplate, productConfig } = useDesignStore();
  const templateAppliedRef = useRef(false);

  useEffect(() => {
    if (!productConfig) return;
    if (jsonObjects && Object.keys(jsonObjects).length) loadFromTemplate(jsonObjects);
    templateAppliedRef.current = true;
  }, [productConfig, jsonObjects, loadFromTemplate]);

  // Same superseded-run guard as DesignDownloadRoot.tsx's own effect (see its
  // comment) — React 18 StrictMode's dev-only double-invoke would otherwise
  // let a cancelled first run's POST race a second, real one.
  const runIdRef = useRef(0);
  useEffect(() => {
    if (!sides.length) return;
    if (!templateAppliedRef.current) return;
    const runId = ++runIdRef.current;

    (async () => {
      const images: Record<string, string> = {};
      for (const side of sides) {
        try {
          images[side.sideKey] = await buildPreviewDataUrl(side, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
        } catch {
          // One side's compositing failing (missing photo, bad JSON, etc.)
          // must not lose every other side's otherwise-good capture.
        }
      }
      if (runId !== runIdRef.current) return;
      if (!Object.keys(images).length) return;

      try {
        await fetch(`/api/designs/${encodeURIComponent(designId)}/capture-guide-preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ images }),
        });
      } catch {
        // Best-effort — the Design Guide PDF falls back to base_high if this
        // never lands, same as before this feature existed.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sides, canvasInstances, sideColorCountJsonCache, designId]);

  return null;
}
